/**
 * Thin wrapper over fetch, shaped to the API's `{ success, message, data }`
 * envelope. Every call either returns the unwrapped `data` or throws an
 * {@link ApiError} carrying the server's machine-readable code.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? '';
const TOKEN_KEY = 'dawa.token';

export interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  code?: string;
  data?: T;
  errors?: Record<string, string[]>;
}

export interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/**
 * A failed request. `code` is the server's stable identifier (EXPIRED,
 * INSUFFICIENT_STOCK, PLAN_LIMIT…) — branch on that, never on `message`, which
 * is prose meant for a person and will change.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly errors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Subscription lapsed. The shell shows a renewal prompt rather than a toast. */
  get isSubscriptionBlocked() {
    return this.status === 402;
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  /** First message for a field, for inline form errors. */
  fieldError(field: string): string | undefined {
    return this.errors?.[field]?.[0];
  }
}

// ── Token storage ───────────────────────────────────────────────────────────
// localStorage rather than a cookie: the Android build runs from file://, where
// cookies for the API origin aren't reliably sent.

export const tokenStore = {
  get: (): string | null => {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set: (token: string) => {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* private mode — the session lives in memory for this tab only */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* nothing to do */
    }
  },
};

/**
 * Called when the server rejects the token. Set by the session provider so a
 * 401 anywhere drops straight to the login screen instead of leaving the UI
 * showing stale data it can no longer refresh.
 */
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  onUnauthorized = handler;
}

// ── Core request ────────────────────────────────────────────────────────────

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // Undefined and empty string mean "not filtering by this", so they're
    // dropped rather than sent as `?search=`.
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }

  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const token = tokenStore.get();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    // A dropped connection is the single most common failure at a counter on
    // patchy mobile data, so it gets its own code rather than a generic throw.
    if (signal?.aborted) throw cause;
    throw new ApiError('Cannot reach the server. Check your connection.', 0, 'NETWORK');
  }

  if (response.status === 401) {
    tokenStore.clear();
    onUnauthorized?.();
    throw new ApiError('Your session has expired. Please log in again.', 401, 'UNAUTHORIZED');
  }

  if (response.status === 204) return undefined as T;

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(
      response.ok ? 'The server sent a response we could not read.' : `Request failed (${response.status}).`,
      response.status,
      'BAD_RESPONSE',
    );
  }

  if (!response.ok || !envelope.success) {
    throw new ApiError(
      envelope.message ?? `Request failed (${response.status}).`,
      response.status,
      envelope.code,
      envelope.errors,
    );
  }

  return envelope.data as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', query, signal }),

  post: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'POST', body, query }),

  put: <T>(path: string, body?: unknown, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'PUT', body, query }),

  delete: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'DELETE', query }),
};

/**
 * URL for a file download. The token rides in the query string because these
 * are opened as plain links or in a WebView, where headers can't be attached —
 * the API accepts `?token=` for exactly this reason.
 */
export function downloadUrl(path: string, query?: Record<string, string | number | undefined>): string {
  const token = tokenStore.get();
  return buildUrl(path, { ...query, token: token ?? undefined });
}
